import { createSelector } from '@reduxjs/toolkit';
import uniqBy from 'lodash/uniqBy';

import { isCurrentPageSel, payloadSel } from '@location/duck/selectors';
import {
  userDataSel as _userDataSel,
  userIdSel,
} from '@modules/user/duck/selectors';
import { ParticipantPositionIdsList } from '@shared/constants';
import { ROUTE_PROFILE } from '@shared/duck/actions';
import {
  hasNewPermissionSel,
  selectedOrganizationSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { isValueTruthy } from '@shared/utils';

import {
  rosterAdultsSel,
  rosterLoadingSel,
  rosterYouthSel,
} from '../../advancement/duck/selectors';
import { childrenSel } from '../../parentGuardian/duck/selectors';
import {
  memberDetailsSel,
  youthInfoSel,
} from '../../youthProfile/duck/selectors';
import { moduleName } from '../constants';

const moduleSel = state => state[moduleName];

export const hobbiesSel = createSelector(_userDataSel, userData => {
  const { extendedProfile } = userData;
  return extendedProfile.find(attr => attr.classification === 'MyHobbies');
});

export const userBioSel = createSelector(_userDataSel, userData => {
  const { extendedProfile } = userData;
  return extendedProfile.find(attr => attr.classification === 'MyBiography');
});

export const activityTypeIdSel = state => payloadSel(state).activityType;

export const youthProfileInfoSel = createSelector(
  youthInfoSel,
  memberDetailsSel,
  (youthInfo, fetchedInfo) => ({ ...youthInfo, ...fetchedInfo }),
);

export const userDataSel = createSelector(
  _userDataSel,
  hobbiesSel,
  memberDetailsSel,
  selectedOrganizationSel,
  (userData, hobbies, details, selectedOrganization) => {
    const {
      profile = {},
      addresses = [],
      phones = [],
      emails = [],
      ypTraining = {},
      subscriptions = [],
      extendedProfile = [],
      sbExtendedProfile = {},
      education = [],
      sbProfile,
    } = userData;
    const address = addresses.find(address => address.isPrimary);
    const phone = phones.find(phone => phone.isPrimary);
    const email = emails.find(email => email.isPrimary);

    let isAdult = isValueTruthy(profile.isAdult);

    if (isAdult && sbProfile?.organizationPositions.length) {
      selectedOrganization.organizationGuid;
      const foundOrg = sbProfile.organizationPositions.find(
        org => org.organizationGuid === selectedOrganization.organizationGuid,
      );
      if (foundOrg && foundOrg.positions?.length) {
        const hasParticipantPosition = foundOrg.positions.some(pos =>
          ParticipantPositionIdsList.includes(Number(pos.id)),
        );
        isAdult = !hasParticipantPosition;
      }
    }

    return {
      isAdult: isAdult,
      boysLife: profile.boysLife,
      cbcCompleted: profile.cbcCompleted,
      isCbcAuthorized: profile.isCbcAuthorized,
      addresses,
      emails,
      phones,
      firstName: profile.firstName,
      middleName: profile.middleName,
      lastName: profile.lastName,
      shortName: profile.shortName,
      memberId: profile.memberId,
      imagePath: profile.imagePath,
      address,
      phone,
      email,
      pictureUrl: profile.pictureUrl,
      hobbies,
      personGuid: profile.personGuid,
      ypTraining,
      subscriptions,
      nickName: profile.nickName,
      dateOfBirth: profile.dateOfBirth,
      gender: profile.gender,
      title: profile.title,
      nameSuffix: profile.nameSuffix,
      sbExtendedProfile,
      dateJoinedBoyScouts: profile.dateJoinedBoyScouts,
      dateJoinedVenturing: profile.dateJoinedVenturing,
      dateJoinedSeaScouts: profile.dateJoinedSeaScouts,
      extendedProfile,
      education,
      currentProgramsAndRanks: details.currentProgramsAndRanks,
      parentsGuardiansInfo: details.parentsGuardiansInfo,
      memberDetails: details, // temporary
    };
  },
);

export const profilePictureSel = createSelector(
  userDataSel,
  userData => userData.pictureUrl,
);

export const profileEditModeSel = state => moduleSel(state).profileEditMode;

export const profileSaveLoadingSel = state =>
  moduleSel(state).profileSaveLoading;

export const isSavingProfileSel = state => moduleSel(state).isSavingProfile;

export { rosterLoadingSel, rosterAdultsSel };
export const rosterYouthCountSel = state => rosterYouthSel(state).length;

export const rosterAdultsCountSel = state => rosterAdultsSel(state).length;

export const canEditJoinedDateFieldsSel = state =>
  hasNewPermissionSel(state, newPermissions.VIEW_EDIT_JOINED_DATE_PROFILE);

const getRelatedChildrenSelV2 = createSelector(
  childrenSel,
  userIdSel,
  children =>
    // Unique list because a child can be on more than 1 unit
    uniqBy(
      children.map(child => ({
        ...child,
        relationshipToYouth: child.relationship,
      })),
      'userId',
    ),
);

export const getRelatedChildrenSel = getRelatedChildrenSelV2;

export const isProfileRouteSel = state =>
  isCurrentPageSel(state, ROUTE_PROFILE);
